import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { map, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalizationModule } from '@nimbus-library/features/localization/localization.module';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { ModalityType } from '@nimbus-global/features/shared/models/domain/modality-type.enum';
import { IUserTasksCapacityService } from '@nimbus-global/features/user-tasks-capacity/services/i-user-tasks-capacity.service';
import { ICapacityViewModel } from '@nimbus-global/features/user-tasks-capacity/models/user-tasks-capacity.model';

@Component({
  standalone: true,
  selector: 'nimbus-user-tp-capacity-button',
  templateUrl: './user-tp-capacity-button.component.html',
  styleUrls: ['./user-tp-capacity-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, SharedUIModule, LocalizationModule],
})
export class UserTpCapacityButtonComponent implements OnInit {
  public readonly ICONS = ICONS;
  public readonly TranslationKeys = TranslationKeys;

  public readonly modalityIcons = {
    [ModalityType.AudioVideo]: ICONS.PHONE,
    [ModalityType.InstantMessaging]: ICONS.COMMENT_ALT,
    [ModalityType.Email]: ICONS.ENVELOPE,
    [ModalityType.ExternalTask]: ICONS.EXTERNAL_TASK,
  };

  public readonly modalityLabels = {
    [ModalityType.AudioVideo]: TranslationKeys.GeneralInitialModalityAudio,
    [ModalityType.InstantMessaging]: TranslationKeys.GeneralInitialModalityInstantMessaging,
    [ModalityType.ExternalTask]: TranslationKeys.GeneralInitialModalityExternalTask,
    [ModalityType.Email]: TranslationKeys.GeneralInitialModalityEmail,
  };

  public readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
  ];

  public readonly isOpen$ = new BehaviorSubject<boolean>(false);
  public tpCapacity$: Observable<ICapacityViewModel | null>;

  public constructor(private readonly capacityService: IUserTasksCapacityService) {}

  public ngOnInit(): void {
    this.capacityService.initialize();
    this.tpCapacity$ = this.capacityService.capacity$.pipe(
      tap((cap) => {
        if (!cap?.taskParallelizationEnabled) {
          this.isOpen$.next(false);
        }
      }),
      map((cap) => {
        if (!cap?.taskParallelizationEnabled) {
          return null;
        }
        return {
          total: { current: cap.total.used, limit: cap.total.limit },
          isTotalFull: !!cap.total.limit && cap.total.used >= cap.total.limit,
          modalities: [
            { type: ModalityType.AudioVideo, entry: cap.audio },
            { type: ModalityType.InstantMessaging, entry: cap.instantMessaging },
            { type: ModalityType.ExternalTask, entry: cap.externalTask },
            { type: ModalityType.Email, entry: cap.email },
          ],
        };
      }),
    );
  }

  public toggle(): void {
    this.isOpen$.next(!this.isOpen$.value);
  }

  public close(): void {
    this.isOpen$.next(false);
  }
}
